<template>
  <b-row class="variable-select-for-condition">
    <b-col cols="12">
      <label class="mb-2 mr-2">
        Show this question if this variable is true:
      </label>
      <b-form-radio-group
        :checked="conditionType"
        @change="onConditionTypeChange"
        buttons
      >
        <b-form-radio
          v-for="{ value, text } in conditionTypesOptions"
          button
          :key="value"
          :value="value"
          :button-variant="value === conditionType ? 'primary' : 'white'"
        >
          {{ text }}
        </b-form-radio>
      </b-form-radio-group>
    </b-col>
    <b-col cols="12">
      <b-btn
        size="sm"
        variant="danger"
        class="mt-2"
        @click="expanded = !expanded"
      >
        Add Condition
      </b-btn>
    </b-col>
    <b-col cols="12" class="mt-2">
      <b-collapse class="text-left" id="vCollapse" v-model="expanded">
        <vue-bootstrap-typeahead
          ref="variableSelector"
          :data="primaryPickList"
          :serializer="
            (v) =>
              `Q${v.question.id}: ${v.question.prompt} / V${v.id}: ${v.input_label}`
          "
          @hit="addVariable($event)"
          placeholder="Type part of question prompt or variable input label..."
        >
          <template slot="suggestion" slot-scope="{ data }">
            <VariableDisplay :variable="data" />
          </template>
        </vue-bootstrap-typeahead>
      </b-collapse>
    </b-col>

    <b-col cols="12">
      <v-wait :for="loaderName">
        <div class="text-center" slot="waiting">
          <b-spinner
            variant="success"
            style="width: 3rem; height: 3rem;"
            label="Large Spinner"
          />
        </div>
        <b-row
          v-for="variable in variablesList"
          :key="variable.id"
          class="mt-3"
        >
          <b-col cols="12">
            <VariableDisplay :variable="variable">
              <b-form-checkbox
                slot="extra-option"
                v-if="variable.question.type === 'YES_NO'"
                class="mt-1"
                switch
                :checked="isUseNoSelected(variable.id)"
                @change="setUseNoValue(variable.id, $event)"
                :value="1"
                :unchecked-value="0"
                >Use No
              </b-form-checkbox>
              <b-col
                slot="remove-icon"
                cols="1"
                class="d-flex align-items-center justify-content-center"
              >
                <i
                  class="mdi mdi-close-circle-outline size-medium phairify-color-red"
                  @click="removeVariable(variable.id)"
                />
              </b-col>
            </VariableDisplay>
            <b-row v-if="variable.question.type === 'SLIDER'" class="mt-1">
              <b-col cols="12">
                <condition-comparator-form
                  :variable="variable"
                  :comparator="comparatorValues[variable.id]"
                  @update-comparator="onVariableComparatorChange"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </v-wait>
    </b-col>
  </b-row>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import VueBootstrapTypeahead from 'vue-typeahead-bootstrap';
import VariableDisplay from '@/components/VariableDisplay.vue';
import ConditionComparatorForm from '@/components/question/condition/ConditionComparatorForm';
import {
  VISIBILITY_CONDITION_TYPE_AND,
  VISIBILITY_CONDITION_TYPE_OR,
} from '@/helpers/questionnaireHelper';
import { GET_CONDITION_VARIABLES_ACTION } from '@/store/conditionVariables';
import { CONDITION_VARIANTS } from '@/store';

export default {
  name: 'VisibilityConditionForm',
  components: {
    VueBootstrapTypeahead,
    VariableDisplay,
    ConditionComparatorForm,
  },
  props: {
    condition: Object,
  },
  data: function () {
    return {
      expanded: false,
      primaryPickList: [],
    };
  },
  computed: {
    conditionTypesOptions: function () {
      return [
        {
          value: VISIBILITY_CONDITION_TYPE_AND,
          text: VISIBILITY_CONDITION_TYPE_AND,
        },
        {
          value: VISIBILITY_CONDITION_TYPE_OR,
          text: VISIBILITY_CONDITION_TYPE_OR,
        },
      ];
    },
    selectedVariableIds: function () {
      return this.condition && Array.isArray(this.condition.condition_variables)
        ? this.condition.condition_variables
        : [];
    },
    conditionType: function () {
      return this.condition && this.condition.condition_type
        ? this.condition.condition_type
        : VISIBILITY_CONDITION_TYPE_AND;
    },
    variablesList: function () {
      return this.primaryPickList.filter(({ id }) =>
        this.selectedVariableIds.includes(id)
      );
    },
    comparatorValues: function () {
      return this.condition && this.condition.comparator_values
        ? this.condition.comparator_values
        : {};
    },
    loaderName: function () {
      return this.$options.name + '_loading';
    },
  },
  methods: {
    addVariable: function (v) {
      const newCondition = {
        ...(this.condition || {}),
        variant: CONDITION_VARIANTS.VISIBILITY,
        condition_type: this.conditionType,
        condition_variables: [...new Set([...this.selectedVariableIds, v.id])],
      };
      this.$emit('set-condition', newCondition);
      this.$refs.variableSelector.inputValue = '';
    },
    removeVariable: function (id) {
      const newCondition = {
        ...(this.condition || {}),
        variant: CONDITION_VARIANTS.VISIBILITY,
        condition_type: this.conditionType,
        condition_variables: this.selectedVariableIds.filter(
          (vId) => vId !== id
        ),
      };
      if (
        newCondition.comparator_values &&
        newCondition.comparator_values[id]
      ) {
        delete newCondition.comparator_values[id];
      }
      if (
        newCondition.condition_variables_params &&
        newCondition.condition_variables_params[id]
      ) {
        delete newCondition.condition_variables_params[id];
      }
      this.$emit('set-condition', newCondition);
    },
    onConditionTypeChange: function (newType) {
      const newCondition = {
        ...(this.condition || {}),
        variant: CONDITION_VARIANTS.VISIBILITY,
        condition_type: newType,
      };
      this.$emit('set-condition', newCondition);
    },
    onVariableComparatorChange: function (variableId, comparator) {
      const newCondition = {
        ...(this.condition || {}),
        variant: CONDITION_VARIANTS.VISIBILITY,
        comparator_values: {
          ...this.comparatorValues,
          [variableId]: comparator,
        },
      };
      this.$emit('set-condition', newCondition);
    },
    loadVariables: async function () {
      this.$wait.start(this.loaderName);
      try {
        this.primaryPickList = await this.$store.dispatch(
          GET_CONDITION_VARIABLES_ACTION
        );
      } catch (e) {
        this.$toasted.error('Unable to upload variables for conditions', {
          duration: 3000,
        });
      } finally {
        this.$wait.end(this.loaderName);
      }
    },
    isUseNoSelected: function (id) {
      return this.condition &&
        this.condition.condition_variables_params &&
        this.condition.condition_variables_params[id] &&
        this.condition.condition_variables_params[id].use_no === 1
        ? 1
        : 0;
    },
    setUseNoValue: function (id, value) {
      const condition = this.condition || {};
      const newCondition = {
        ...condition,
        variant: CONDITION_VARIANTS.VISIBILITY,
        condition_variables_params: {
          ...(condition.condition_variables_params || {}),
          [id]: { use_no: value },
        },
      };
      this.$emit('set-condition', newCondition);
    },
  },
  created: function () {
    this.loadVariables();
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
@import '../../../../sass/variables_mixins';
.variable-select-for-condition {
  .list-group-item-action {
    color: inherit !important;
    &:hover,
    &:focus-visible {
      background-color: $light-blue;
    }
  }
  i.mdi.mdi-close-circle-outline {
    &:hover {
      cursor: pointer;
    }
  }
}
</style>
